import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import TextAlign from "@tiptap/extension-text-align";
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  List,
  ListOrdered,
  AlignLeft,
  AlignCenter,
  AlignRight,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./tabs";
import { Textarea } from "./textarea";
import { Button } from "./button";
import { cn } from "~/lib/utils";

/** Treat RTE output as empty when it only contains tags/whitespace (e.g. <p></p>). */
function normalizeRteValue(html: string): string {
  if (html == null || typeof html !== "string") return "";
  const stripped = html.replace(/<[^>]*>/g, "").trim();
  return stripped === "" ? "" : html;
}

function TipTapEditor({
  value,
  onChange,
  onBlur,
  maxChars,
  name,
  hasError,
  errorMessage,
}: {
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  maxChars?: number;
  name: string;
  hasError?: boolean;
  errorMessage?: string;
}) {
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [isUnderline, setIsUnderline] = useState(false);
  const [isBulletList, setIsBulletList] = useState(false);
  const [isOrderedList, setIsOrderedList] = useState(false);
  const [isAlignLeft, setIsAlignLeft] = useState(false);
  const [isAlignCenter, setIsAlignCenter] = useState(false);
  const [isAlignRight, setIsAlignRight] = useState(false);

  const [showMaxCharsWarning, setShowMaxCharsWarning] = useState(false);
  const warningTimeout = useRef<NodeJS.Timeout | null>(null);

  const lastValidContent = useRef<string>(value || "");

  const editor = useEditor({
    extensions: [
      StarterKit,
      TextAlign.configure({
        types: ["heading", "paragraph"],
      }),
    ],
    content: value,

    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      const text = editor.getText();
      // Check character limit based on plain text
      if (maxChars && text.length > maxChars) {
        // Revert to last valid content if limit exceeded
        editor.commands.setContent(lastValidContent.current);

        setShowMaxCharsWarning(true);

        if (warningTimeout.current) {
          clearTimeout(warningTimeout.current);
        }

        warningTimeout.current = setTimeout(() => {
          setShowMaxCharsWarning(false);
        }, 3000);
        return;
      }
      const valueToStore = normalizeRteValue(html);
      lastValidContent.current = valueToStore;
      onChange(valueToStore);
    },
    editorProps: {
      attributes: {
        class: `list-styling resize-y overflow-auto border-black border-2 placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 aria-invalid:border-destructive field-sizing-content min-h-16 w-full rounded-md border bg-transparent px-3 py-2 text-base shadow-xs transition-[color,box-shadow] outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50 md:text-sm ${hasError ? "border-destructive focus-visible:ring-destructive/20" : ""}`,
        ...(hasError && { "aria-invalid": "true" }),
        ...(errorMessage && { "aria-describedby": `${name}-error` }),
      },
    },
  });

  useEffect(() => {
    const currentHtml = editor?.getHTML() ?? "";
    if (
      editor &&
      normalizeRteValue(value ?? "") !== normalizeRteValue(currentHtml)
    ) {
      const newContent = value ?? "";
      editor.commands.setContent(newContent);
      lastValidContent.current = newContent;
    }
  }, [value, editor]);

  useEffect(() => {
    const updateBoldState = () => {
      const isActive = editor.isActive("bold");
      const hasStoredBold = editor.state.storedMarks?.some(
        (m) => m.type.name === "bold",
      );
      setIsBold(isActive || hasStoredBold || false);
    };

    const updateItalicState = () => {
      const isActive = editor.isActive("italic");
      const hasStoredItalic = editor.state.storedMarks?.some(
        (m) => m.type.name === "italic",
      );
      setIsItalic(isActive || hasStoredItalic || false);
    };

    const updateUnderlineState = () => {
      const isActive = editor.isActive("underline");
      const hasStoredUnderline = editor.state.storedMarks?.some(
        (m) => m.type.name === "underline",
      );
      setIsUnderline(isActive || hasStoredUnderline || false);
    };

    const updateBulletListState = () => {
      setIsBulletList(editor.isActive("bulletList"));
    };

    const updateOrderedListState = () => {
      setIsOrderedList(editor.isActive("orderedList"));
    };

    const updateAlignStates = () => {
      setIsAlignLeft(editor.isActive({ textAlign: "left" }));
      setIsAlignCenter(editor.isActive({ textAlign: "center" }));
      setIsAlignRight(editor.isActive({ textAlign: "right" }));
    };

    const updateAllStates = () => {
      updateBoldState();
      updateItalicState();
      updateUnderlineState();
      updateBulletListState();
      updateOrderedListState();
      updateAlignStates();
    };

    updateAllStates();

    editor.on("selectionUpdate", updateAllStates);
    editor.on("transaction", updateAllStates);

    return () => {
      editor.off("selectionUpdate", updateAllStates);
      editor.off("transaction", updateAllStates);
    };
  }, [editor]);

  if (!editor) {
    return null;
  }

  const currentTextLength = editor.getText().length;

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-col gap-4">
        <div className="flex flex-wrap gap-1">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => editor.chain().focus().toggleBold().run()}
            disabled={!editor.can().chain().focus().toggleBold().run()}
            className={cn("h-8 w-8", isBold && "bg-accent")}
          >
            <Bold className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => editor.chain().focus().toggleItalic().run()}
            disabled={!editor.can().chain().focus().toggleItalic().run()}
            className={cn("h-8 w-8", isItalic && "bg-accent")}
          >
            <Italic className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => editor.chain().focus().toggleUnderline().run()}
            disabled={!editor.can().chain().focus().toggleUnderline().run()}
            className={cn("h-8 w-8", isUnderline && "bg-accent")}
          >
            <UnderlineIcon className="h-4 w-4" />
          </Button>
          <div className="w-px h-6 bg-border mx-1" />
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            className={cn("h-8 w-8", isBulletList && "bg-accent")}
          >
            <List className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            className={cn("h-8 w-8", isOrderedList && "bg-accent")}
          >
            <ListOrdered className="h-4 w-4" />
          </Button>
          <div className="w-px h-6 bg-border mx-1" />
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => editor.chain().focus().setTextAlign("left").run()}
            className={cn("h-8 w-8", isAlignLeft && "bg-accent")}
          >
            <AlignLeft className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => editor.chain().focus().setTextAlign("center").run()}
            className={cn("h-8 w-8", isAlignCenter && "bg-accent")}
          >
            <AlignCenter className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => editor.chain().focus().setTextAlign("right").run()}
            className={cn("h-8 w-8", isAlignRight && "bg-accent")}
          >
            <AlignRight className="h-4 w-4" />
          </Button>
        </div>
        <EditorContent editor={editor} />
      </div>
      <div className="flex justify-between gap-8">
        <span>
          {errorMessage && (
            <p id={`${name[0]}-error`} className="text-destructive text-sm text-red-500">
              {errorMessage}
            </p>
          )}

          {showMaxCharsWarning && (
            <p className="text-sm text-amber-600">
              Maximale Zeichenanzahl überschritten
            </p>
          )}
        </span>
        {maxChars ? (
          <p className="text-neutral-darkest text-right">
            {currentTextLength}/{maxChars}
          </p>
        ) : null}
      </div>
    </div>
  );
}

export function MultipleVersionTextarea({
  name,
  value,
  label,
  errors,
  maxChars,
  onChange,
  onBlur,
  enableRTE = false,
  hideEnglish = false,
}: {
  name: string[];
  value: string[];
  label: string[];
  errors?: {
    german?: string;
    english?: string;
  };
  maxChars?: number;
  onChange: (value: string[]) => void;
  onBlur?: () => void;
  enableRTE?: boolean;
  hideEnglish?: boolean;
}) {
  // Ensure all values are strings (handle undefined/null)
  const safeValue = value.map((v) => (v ?? "") as string);

  const [showMaxCharsWarning, setShowMaxCharsWarning] = useState<
    Record<number, boolean>
  >({});
  const warningTimeouts = useRef<Record<number, NodeJS.Timeout>>({});

  const triggerMaxCharsWarning = (index: number) => {
    setShowMaxCharsWarning((prev) => ({ ...prev, [index]: true }));

    if (warningTimeouts.current[index]) {
      clearTimeout(warningTimeouts.current[index]);
    }

    warningTimeouts.current[index] = setTimeout(() => {
      setShowMaxCharsWarning((prev) => ({ ...prev, [index]: false }));
    }, 3000);
  };

  const [activeTab, setActiveTab] = useState(name[0]);

  // If hideEnglish is true, only show the first (German) tab without tabs UI
  if (hideEnglish) {
    const errorMessage = errors?.german;
    const hasError = !!errorMessage;

    return (
      <div>
        {enableRTE ? (
          <TipTapEditor
            errorMessage={errorMessage}
            hasError={hasError}
            name={name[0]}
            value={safeValue[0] ?? ""}
            onChange={(newValue) => {
              // Always send both values, but english is empty when hidden
              onChange([newValue, ""]);
            }}
            onBlur={onBlur}
            maxChars={maxChars}
          />
        ) : (
          <div className="flex flex-col gap-2">
            <Textarea
              name={name[0]}
              value={safeValue[0] ?? ""}
              onChange={(e) => {
                if (maxChars && e.target.value.length > maxChars) {
                  triggerMaxCharsWarning(0);
                  return;
                }

                onChange([e.target.value, ""]);
              }}
              onBlur={onBlur}
              variant="outline"
              {...(hasError && { "aria-invalid": true })}
              {...(errorMessage && { "aria-describedby": `${name[0]}-error` })}
              className={cn(hasError && "border-destructive")}
            />
            <div className="flex justify-between gap-8">
              <span>
                {errorMessage && (
                  <p id={`${name[0]}-error`} className="text-destructive text-sm text-red-500">
                    {errorMessage}
                  </p>
                )}

                {showMaxCharsWarning[0] && (
                  <p className="text-sm text-amber-600">
                    Maximale Zeichenanzahl überschritten
                  </p>
                )}
              </span>
              {maxChars ? (
                <p className="text-neutral-darkest text-right">
                  {(safeValue[0] ?? "").length}/{maxChars}
                </p>
              ) : null}
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div>
      <Tabs
        className={"gap-4"}
        defaultValue={name[0]}
        value={activeTab}
        onValueChange={setActiveTab}
      >
        <TabsList>
          {label.map((l, index) => (
            <TabsTrigger key={index} value={name[index]}>
              {l}
            </TabsTrigger>
          ))}
        </TabsList>
        {label.map((l, index) => {
          const isGerman = index === 0;
          const errorMessage = isGerman ? errors?.german : errors?.english;
          const hasError = !!errorMessage;
          const isActiveTab = activeTab === name[index];

          return (
            <TabsContent key={index} value={name[index]}>
              {enableRTE ? (
                <TipTapEditor
                  name={name[index]}
                  value={safeValue[index] ?? ""}
                  onChange={(newValue) => {
                    const updatedValue = [...safeValue];
                    updatedValue[index] = newValue;
                    onChange(updatedValue);
                  }}
                  onBlur={onBlur}
                  maxChars={maxChars}
                  hasError={isActiveTab ? hasError : false}
                  errorMessage={isActiveTab ? errorMessage : undefined}
                />
              ) : (
                <div className="flex flex-col gap-2">
                  <Textarea
                    name={name[index]}
                    value={safeValue[index] ?? ""}
                    onChange={(e) => {
                      if (maxChars && e.target.value.length > maxChars) {
                        triggerMaxCharsWarning(index);
                        return;
                      }

                      const updated = [...safeValue];
                      updated[index] = e.target.value;
                      onChange(updated);
                    }}
                    onBlur={onBlur}
                    variant="outline"
                    {...(isActiveTab && hasError && { "aria-invalid": true })}
                    {...(isActiveTab &&
                      errorMessage && {
                      "aria-describedby": `${name[index]}-error`,
                    })}
                    className={cn(isActiveTab && hasError && "border-destructive")}
                  />
                  <div className="flex justify-between gap-8">
                    <span>
                      {isActiveTab && errorMessage && (
                        <p className="text-destructive text-sm text-red-500">
                          {errorMessage}
                        </p>
                      )}

                      {isActiveTab && showMaxCharsWarning[index] && (
                        <p className="text-sm text-amber-600">
                          Maximale Zeichenanzahl überschritten
                        </p>
                      )}
                    </span>
                    {maxChars ? (
                      <p className="text-neutral-darkest text-right">
                        {(safeValue[index] ?? "").length}/{maxChars}
                      </p>
                    ) : null}
                  </div>
                </div>
              )}
            </TabsContent>
          );
        })}
      </Tabs>
    </div>
  );
}
